import React, { PureComponent, createContext, useCallback, useContext, useRef } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import ErrorBoundary from 'components/error-boundary-static';
import { useLogger } from 'state/logger';
import { LogLevel } from 'utils/logger';

import { useLocationContext } from '../location';

interface IErrorBoundaryProps extends IBaseProps {
  onError: (e: Error, meta: object) => void;
}

export interface IErrorCtx {
  handleError: (e: Error, meta?: object) => void;
  setCurrentOrderId: (id: string) => void;
}

export const ErrorContext = createContext<IErrorCtx>(({} as any) as IErrorCtx);
export const useErrorContext = () => useContext(ErrorContext);

/**
 * `componentDidCatch` is only available
 * on Class components :(
 **/
class TopLevelErrorBoundary extends PureComponent<IErrorBoundaryProps> {
  state = {
    hasError: false,
  };

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: any) {
    const { onError } = this.props;
    onError(error, { info, level: LogLevel.fatal });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorBoundary />;
    }
    return this.props.children;
  }
}

export function ErrorsProvider({ children }: IBaseProps) {
  const locationContext = useLocationContext();
  const logger = useLogger();
  const location = locationContext ? locationContext.location : { pathname: '/' };
  const orderId = useRef<string>('');

  const setCurrentOrderId = useCallback((serverOrderId: string) => {
    orderId.current = serverOrderId;
  }, []);

  const handleError = useCallback(
    (error: Error, meta: object = {}) => {
      const errorMeta = {
        ...meta,
        'Transaction RBI Cloud Order ID': orderId.current,
        'Source Page': location.pathname,
      };

      logger.error({ error, ...errorMeta });
    },
    [location.pathname, logger]
  );

  return (
    <ErrorContext.Provider value={{ handleError, setCurrentOrderId }}>
      <TopLevelErrorBoundary onError={handleError}>{children}</TopLevelErrorBoundary>
    </ErrorContext.Provider>
  );
}
